import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { apiRequest } from "../../lib/api";
import type { Person } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { changesQuery, configurationsQuery } from "../configs/queries";

export const personsQuery = (workspaceId: string, deleted: boolean) =>
  queryOptions({
    queryKey: ["persons", workspaceId, deleted ? "deleted" : "live"] as const,
    queryFn: () => apiRequest<Person[]>(paths.persons(workspaceId, deleted)),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

export function usePersons(workspaceId: string, deleted: boolean) {
  return useQuery(personsQuery(workspaceId, deleted));
}

/**
 * Person ids that changed since the last download of any live configuration. Built from
 * each configuration's changes list, so the roster badge agrees with the configuration badges.
 */
export function useChangedPersonIds(workspaceId: string): Set<string> {
  const configurations = useQuery(configurationsQuery(workspaceId, false));
  const changes = useQueries({
    queries: (configurations.data ?? []).map((configuration) =>
      changesQuery(workspaceId, configuration.id),
    ),
  });
  const ids = new Set<string>();
  for (const result of changes) {
    const data = result.data;
    for (const personId of data?.changedPersonIds ?? []) ids.add(personId);
  }
  return ids;
}
